import React from 'react';
import { useRecoilState } from 'recoil';
import { useMutation } from 'react-query';
import { Box, Button, CircularProgress, Stack, Table, TableBody, TableContainer, TableHead, Typography } from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import axios, { endPoints } from 'api/request';
import { styled } from '@mui/material/styles';
// import { format } from 'date-fns';
// import { ReactComponent as EditIcon } from 'assets/images/edit.svg';
import toast from 'react-hot-toast';
import Modal from 'components/modal';
import Empty from 'components/empty';
import { handleReset } from 'pages/user-preferences/utils/getPayload';
import { convertDateToUTC } from 'pages/user-preferences/utils/dateUtils';
import { commonStyles as styles } from 'assets/style';
import Btn from 'components/button';
import Form from 'pages/user-preferences/components/delegates/Form';
import { OrderPayload, delegateListState, toggleCreateDelegate, toggleDeleteDelegate } from 'pages/user-preferences/store';
import style from 'pages/user-preferences/style';
import { ConfirmDeleteDelegateModal } from './confirmDeleteDelegateModal';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        color: theme.palette.blue.strongDark,
        font: theme.typography.fontSize.$font_small_bold,
        padding: '15px',
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
    },
    [`&.${tableCellClasses.body}`]: {
        font: theme.typography.fontSize.$font_small_regular,
        color: theme.palette.cmpBlack.doply,
        padding: '15px',
    },
}));
const StyledTableRow = styled(TableRow)(({ theme }) => ({
    backgroundColor: '#ffffff',
    boxShadow: 'inset 0 -2px #d7e5f8',
    '&:hover': {
        backgroundColor: theme.palette.cyan['90'],
    },
}));

export const DelegatesList = () => {
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [delegatesList, setDelegatesList] = useRecoilState(delegateListState);
    const [modal, setModal] = useRecoilState(toggleCreateDelegate);
    const [confirmModal, setConfirmModal] = useRecoilState(toggleDeleteDelegate);
    const fetchDelegateListApi = useMutation(() => axios.get(endPoints?.staticLookups?.delegates));
    const createDelegateApi = useMutation((payload) => axios.post(`${endPoints?.staticLookups?.delegates}`, payload));
    const updateDelegateApi = useMutation((payload) => axios.put(`${endPoints?.staticLookups?.delegates}/${order?.delegateOwnerDetails?.gpid}`, payload));

    const convertTimestampToUTC = (timestamp) => {
        if (!timestamp) return null;
        const date = new Date(timestamp * 1000);
        return date.toLocaleDateString('en-GB', {
            timeZone: 'UTC',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };
    const handleSubmit = () => {
        const payload = {
            title: order?.delegateTitle,
            delegateId: order?.delegateOwnerDetails?.gpid,
            name: order?.delegateOwnerDetails?.name,
            startDate: convertDateToUTC(order?.startDate) || null,
            endDate: convertDateToUTC(order?.endDate, true) || null,
            businessJustification: order?.businessJustification || null,
        };
        if (modal.type === 'create') {
            createDelegateApi.mutate(payload, {
                onSuccess: (data) => {
                    setModal({ isOpen: false, type: 'create' });
                    fetchDelegateListApi?.mutate();
                    toast.success(`${data?.messages?.[0]}`);
                },
            });
        }
        if (modal.type === 'edit') {
            updateDelegateApi.mutate(payload, {
                onSuccess: (data) => {
                    setModal({ isOpen: false, type: null });
                    const updatedList = delegatesList?.map((delegate) =>
                        delegate?.delegateId === payload?.delegateId
                            ? { ...delegate, ...payload } // Update the edited delegate
                            : delegate
                    );
                    setDelegatesList(updatedList); // Update the delegates list
                    toast.success(`${data?.messages?.[0]}`);
                },
            });
        }
    };

    React.useEffect(() => {
        fetchDelegateListApi?.mutate();
    }, []);

    React.useEffect(() => {
        if (fetchDelegateListApi?.data?.data?.delegates) {
            setDelegatesList(fetchDelegateListApi?.data?.data?.delegates);
        }
    }, [fetchDelegateListApi?.data]);

    const resetOrder = () => {
        setOrder(handleReset());
    };

    const handleCloseModal = () => {
        setModal({ isOpen: false, type: 'create' });
    };

    const handleDelete = (id, schdlID) => {
        setConfirmModal({
            ...confirmModal,
            type: 'delete',
            isOpen: true,
            message: 'Do you want to delete the Delegate?',
            id,
            scheduleId: schdlID,
        });
    };

    React.useEffect(() => {
        const rwcElement = document.getElementById('rwc');
        if (rwcElement) {
            rwcElement.style.display = modal?.isOpen ? 'none' : 'block';
        }
    }, [modal?.isOpen]);

    const isDateRangeValid = () => {
        const normalizeDate = (date) => {
            const normalized = new Date(date);
            normalized.setHours(0, 0, 0, 0);
            return normalized;
        };

        const isValidDate = (date) => {
            return !Number.isNaN(new Date(date).getTime());
        };

        if (!isValidDate(order?.startDate) || !isValidDate(order?.endDate)) {
            return false;
        }

        const startDateminDate = normalizeDate(new Date());
        const startDatemaxDate = new Date(startDateminDate);
        startDatemaxDate.setMonth(startDateminDate.getMonth() + 6);

        const endDateminDate = order?.startDate ? normalizeDate(order?.startDate) : startDateminDate;
        const endDatemaxDate = new Date(endDateminDate);
        endDatemaxDate.setMonth(endDateminDate.getMonth() + 6);

        // Validate startDate
        if (order?.startDate) {
            const startDate = normalizeDate(order?.startDate);
            if (startDate < startDateminDate || startDate > startDatemaxDate) {
                return false;
            }
        }

        // Validate endDate
        if (order?.endDate) {
            const endDate = normalizeDate(order?.endDate);
            if (endDate < endDateminDate || endDate > endDatemaxDate) {
                return false;
            }

            // Allow if startDate and endDate are the same
            if (order?.startDate && endDate.getTime() === normalizeDate(order.startDate).getTime()) {
                return true;
            }
        }

        return true;
    };
    return (
        <Box className="contentContainer">
            <Box display="flex" flexDirection="column" justifyContent="flex-start" alignItems="flex-start">
                <Typography sx={{ fontSize: '22px', fontWeight: 600 }} data-testid="delegates-text">
                    Delegates
                </Typography>
                <Typography sx={{ fontSize: '16px', color: '#616161' }}>You can set delegates to validate and approve the orders on your behalf</Typography>
            </Box>
            <Box overflow="auto" display="flex" flexDirection="column" pt={1.7} pb={1} sx={{ color: '#ffffff' }}>
                <TableContainer sx={{ marginTop: '4px' }}>
                    <Table sx={{ marginTop: '-9px', borderSpacing: '0 4px', minWidth: 500, height: 'auto' }} size="small" stickyHeader>
                        <TableHead key="head">
                            <TableRow sx={{ height: '36px' }}>
                                <StyledTableCell width="15%">TITLE</StyledTableCell>
                                <StyledTableCell width="20%">NAME</StyledTableCell>
                                <StyledTableCell width="10%">FROM</StyledTableCell>
                                <StyledTableCell width="10%">TO</StyledTableCell>
                                <StyledTableCell width="35%">BUSINESS JUSTIFICATION</StyledTableCell>
                                <StyledTableCell align="center" width="10%">
                                    ACTIONS
                                </StyledTableCell>
                            </TableRow>
                        </TableHead>
                        {fetchDelegateListApi?.isLoading && (
                            <TableBody>
                                <StyledTableRow sx={style?.list?.emptyRow?.row}>
                                    <StyledTableCell sx={style?.list?.emptyRow?.cell} colSpan="7">
                                        <Empty isLoading={fetchDelegateListApi?.isLoading} msg="Loading..." color="#000000" />
                                    </StyledTableCell>
                                </StyledTableRow>
                            </TableBody>
                        )}
                        {!fetchDelegateListApi?.isLoading && (
                            <TableBody>
                                {delegatesList?.map((delegate, index) => (
                                    <StyledTableRow sx={{ height: '36px' }} className="animate-opacity" key={`row-${delegate?.delegateId}`}>
                                        <StyledTableCell width="15%" data-testid={`delegate-title-field-${index}`}>
                                            {delegate?.schedules?.at(0)?.title || '-'}
                                        </StyledTableCell>
                                        <StyledTableCell width="20%" data-testid={`delegate-name-field-${index}`}>
                                            {delegate?.name}
                                        </StyledTableCell>
                                        <StyledTableCell width="10%">
                                            {delegate?.schedules?.at(0)?.startDate ? convertTimestampToUTC(delegate?.schedules?.at(0)?.startDate) : '-'}
                                        </StyledTableCell>
                                        <StyledTableCell width="10%">
                                            {delegate?.schedules?.at(0)?.endDate ? convertTimestampToUTC(delegate?.schedules?.at(0)?.endDate) : '-'}
                                        </StyledTableCell>
                                        <StyledTableCell
                                            width="35%"
                                            data-testid={`delegate-justification-field-${index}`}
                                            sx={{
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                                maxWidth: 320,
                                                verticalAlign: 'top',
                                            }}
                                        >
                                            {delegate?.schedules?.at(0)?.businessJustification || '-'}
                                        </StyledTableCell>
                                        <StyledTableCell width="10%">
                                            <Box display="flex" justifyContent="center">
                                                {/* <Box
                                                    sx={{
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: '27px',
                                                        height: '27px',
                                                        background: '#0047BA',
                                                        marginRight: '8px',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <EditIcon
                                                        style={{
                                                            width: '14px',
                                                            height: '12px',
                                                            color: '#FFFFFF',
                                                            cursor: 'pointer',
                                                        }}
                                                        data-testid={`edit-icon-${index}`}
                                                        onClick={() => {
                                                            // Set the selected delegate's data to the order state
                                                            setOrder({
                                                                delegateTitle: delegate?.schedules?.at(0)?.title || '',
                                                                delegateOwnerDetails: {
                                                                    gpid: delegate.delegateId,
                                                                    name: delegate.name,
                                                                },
                                                                startDate: delegate?.schedules?.at(0)?.startDate
                                                                    ? format(new Date(delegate?.schedules?.at(0)?.startDate * 1000), 'yyyy-MM-dd')
                                                                    : null,
                                                                endDate: delegate?.schedules?.at(0)?.endDate
                                                                    ? format(new Date(delegate?.schedules?.at(0)?.endDate * 1000), 'yyyy-MM-dd')
                                                                    : null,
                                                                businessJustification: delegate?.schedules?.at(0)?.businessJustification || '',
                                                                scheduleId: delegate?.schedules?.at(0)?.scheduleId || '',
                                                            });
                                                            setModal({ isOpen: true, type: 'edit' });
                                                        }}
                                                    />
                                                </Box> */}
                                                <Box
                                                    sx={{
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: '27px',
                                                        height: '27px',
                                                        background: '#DB1E36',
                                                        cursor: 'pointer',
                                                    }}
                                                    data-testid={`delete-icon-${index}`}
                                                    onClick={() => handleDelete(delegate?.delegateId, delegate?.schedules?.at(0)?.scheduleId)}
                                                >
                                                    <DeleteIcon
                                                        style={{
                                                            fontSize: '18px',
                                                            color: '#FFFFFF',
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        )}
                    </Table>

                    {!fetchDelegateListApi?.isLoading && (!delegatesList || !delegatesList?.length) && (
                        <Box display="flex" justifyContent="center" mt={2} whiteSpace="nowrap" color="#000000" fontSize="14px" fontWeight={500}>
                            Add your Delegate
                        </Box>
                    )}
                    <Box display="flex" justifyContent="flex-end" alignItems="center" padding="20px 3px">
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => {
                                resetOrder();
                                setModal({ isOpen: true, type: 'create' });
                            }}
                            sx={style.createApprovalButton}
                            data-testid="create-delegate-btn"
                            disableRipple
                            startIcon={<AddIcon />}
                        >
                            Add Delegate
                        </Button>
                    </Box>
                </TableContainer>
            </Box>
            <ConfirmDeleteDelegateModal setDelegateList={setDelegatesList} />
            <Modal open={modal.isOpen} handleClose={handleCloseModal} title="Delegates">
                <Box sx={style.delegateModal.wrapper} data-testid="delegates-model">
                    <Box sx={style.delegateModal.contentWrapper}>
                        <Box key="delegate" sx={style.delegateModal.rowWrapper} className="space-x-2 mb-5 animate-opacity">
                            <Box sx={style.delegateModal.delegateWrapper}>
                                <Stack direction="column" spacing={2}>
                                    <Form.DelegateTitle />
                                    <Form.DelegateName />
                                    <Form.FromDate />
                                    <Form.ToDate />
                                    <Form.BusinessJustification />
                                </Stack>
                            </Box>
                        </Box>
                    </Box>
                    <Box className="footer" sx={style.delegateModal.actionWrapper} gap={2}>
                        <Btn
                            className="btn-disabled custom-btn"
                            variant="contained"
                            onClick={() => handleSubmit()}
                            data-testid="save-btn"
                            sx={styles.modalSaveButton}
                            disabled={
                                createDelegateApi?.isLoading ||
                                updateDelegateApi?.isLoading ||
                                !order?.delegateOwnerDetails?.name ||
                                !order?.delegateOwner ||
                                !order?.delegateTitle ||
                                !order?.businessJustification ||
                                !order?.startDate ||
                                !order?.endDate ||
                                !isDateRangeValid()
                            }
                        >
                            Save
                            {(createDelegateApi?.isLoading || updateDelegateApi?.isLoading) && <CircularProgress sx={{ ml: 1 }} color="inherit" size={14} />}
                        </Btn>
                        <Btn
                            className="custom-btn"
                            onClick={() => {
                                resetOrder();
                                setModal({ isOpen: false, type: 'create' });
                            }}
                            variant="outlined"
                            layout="outline"
                            data-testid="cancel-btn"
                            sx={styles.modalCancelButton}
                        >
                            Cancel
                        </Btn>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default React.memo(DelegatesList);
